'use client'

import { useState, useEffect } from 'react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'

import { getStripe, stripeElementsOptions, paymentElementOptions } from '@/lib/stripe/client'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react'

interface StripePaymentFormProps {
  amount: number
  onSuccess: (paymentIntentId: string) => void
  onError: (error: string) => void
  disabled?: boolean
}

function StripePaymentForm({
  amount,
  onSuccess,
  onError,
  disabled = false
}: StripePaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (stripe && elements) {
      setIsReady(true)
    }
  }, [stripe, elements])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements || isProcessing || disabled) {
      return
    }

    setIsProcessing(true)
    setErrorMessage(null)
    setValidationError(null)

    try {
      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit()
      if (submitError) {
        const userMessage = submitError.message || 'Please check your payment details.'
        setErrorMessage(userMessage)
        onError(userMessage)
        return
      }

      // Confirm payment with Stripe
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required'
      })

      if (error) {
        // Handle payment errors
        const userMessage = error.message || 'Payment failed. Please try again.'
        setErrorMessage(userMessage)
        onError(userMessage)
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment succeeded
        onSuccess(paymentIntent.id)
      } else {
        // Unexpected state
        setErrorMessage('Payment status unclear. Please contact support.')
        onError('Payment status unclear')
      }
    } catch (error) {
      setErrorMessage('An unexpected error occurred. Please try again.')
      onError('An unexpected error occurred')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Payment Element */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-white">Payment Details</label>
        <div className="min-h-[200px]">
          <PaymentElement
            options={paymentElementOptions}
            onReady={() => setIsReady(true)}
            onChange={(event) => {
              if (event.complete) {
                setValidationError(null)
              }
            }}
          />
        </div>
      </div>

      {/* Error Display - Show validation errors with priority, fallback to payment errors */}
      {(validationError || errorMessage) && (
        <Alert className="border-red-500 bg-red-500/10 animate-in fade-in-0 slide-in-from-top-1">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <AlertDescription className="text-red-400 font-medium">
            {validationError || errorMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={!isReady || isProcessing || !stripe || !elements || disabled || !!validationError}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-lg py-3"
      >
        {isProcessing ? (
          <div className="flex items-center">
            <Loader2 className="animate-spin h-4 w-4 mr-2" />
            Processing Payment...
          </div>
        ) : (
          `Pay €${(amount / 100).toFixed(2)}`
        )}
      </Button>

      {/* Security Notice */}
      <div className="text-center">
        <p className="text-white/60 text-xs">
          <CheckCircle className="w-3 h-3 inline mr-1" />
          Secure payment powered by Stripe. Your payment information is encrypted and secure.
        </p>
      </div>
    </form>
  )
}

interface StripeElementsWrapperProps {
  clientSecret: string
  amount: number
  onSuccess: (paymentIntentId: string) => void
  onError: (error: string) => void
  disabled?: boolean
}

export default function StripeElementsWrapper({
  clientSecret,
  amount,
  onSuccess,
  onError,
  disabled = false
}: StripeElementsWrapperProps) {
  const [stripe, setStripe] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    const loadStripe = async () => {
      try {
        const stripeInstance = await getStripe()
        if (!stripeInstance) {
          throw new Error('Failed to load Stripe')
        }
        setStripe(stripeInstance)
      } catch (error) {
        console.error('Failed to load Stripe:', error)
        setLoadError('Failed to load payment system. Please refresh and try again.')
      } finally {
        setIsLoading(false)
      }
    }

    loadStripe()
  }, [])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="animate-spin h-8 w-8 mx-auto mb-2 text-blue-500" />
            <p className="text-white/80">Loading payment form...</p>
          </div>
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <Alert className="border-red-500 bg-red-500/10">
        <AlertCircle className="h-4 w-4 text-red-500" />
        <AlertDescription className="text-red-400">{loadError}</AlertDescription>
      </Alert>
    )
  }

  if (!stripe || !clientSecret) {
    return (
      <Alert className="border-yellow-500 bg-yellow-500/10">
        <AlertCircle className="h-4 w-4 text-yellow-500" />
        <AlertDescription className="text-yellow-400">
          Payment system not available. Please refresh and try again.
        </AlertDescription>
      </Alert>
    )
  }

  const elementsOptions = {
    clientSecret,
    ...stripeElementsOptions
  }

  return (
    <Elements stripe={stripe} options={elementsOptions}>
      <StripePaymentForm
        amount={amount}
        onSuccess={onSuccess}
        onError={onError}
        disabled={disabled}
      />
    </Elements>
  )
}
